import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useDispatch } from "react-redux"
import { refundOrderThunk, useOrder } from "../../redux/ordersSlice"
import { useProduct } from "../../redux/productsSlice"
import { useSku } from "../../redux/skusSlice"
import { AppDispatch } from "../../redux/store"
import { NfaOrderStatus, Order } from "../../api/models"
import Spinner from "../widgets/Spinner"
import { formatPrice } from "../widgets/UpgradeInfo"
import { backdropDismiss } from "../../utils/backdropDismiss"

export type RefundState = "eligible" | "expired" | "refunded"

// Derived at render time from the server-supplied deadline, never cached as
// a flag: an order fetched on day 13 must stop reading as refundable on day
// 14 without a refetch. The backend re-checks on POST.
export function refundState(order: Order, now: number = Date.now()): RefundState {
  if (order.status === NfaOrderStatus.Refunded)
    return "refunded"
  if (now >= Date.parse(order.refundableUntil))
    return "expired"
  return "eligible"
}

function formatDate(d?: string | null) {
  if (!d) return "-"
  return new Date(d).toLocaleDateString()
}

// One product name inside the restored-entitlement line, resolved by id.
function RestoredProductName({ productId, accessToken, last }: { productId: string, accessToken: string, last: boolean }) {
  const { data: product } = useProduct(productId, accessToken)
  return <>{product?.name ?? "…"}{!last && ", "}</>
}

// One entitlement a refund would bring back: its sku's name with the
// products whose licenses get re-deposited.
function RestoredEntitlementItem({ skuId, productIds, accessToken }: { skuId: string, productIds: string[], accessToken: string }) {
  const { data: sku } = useSku(skuId)
  return (
    <li>
      <strong>{sku?.name ?? "…"}</strong>
      {productIds.length > 0 && (
        <span style={{ color: "var(--nfa-light-grey)" }}>
          {" — "}
          {productIds.map((id, i) => (
            <RestoredProductName key={id} productId={id} accessToken={accessToken} last={i === productIds.length - 1} />
          ))}
        </span>
      )}
    </li>
  )
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>()
  const session = useSession()
  const accessToken = session.accessToken || ""
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)

  const { data: order } = useOrder(id!, accessToken)

  if (order) {
    const state = refundState(order)
    return (
      <div className="page">
        <h1 className="page-title">Order</h1>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Status</span>
            <span className="detail-value">
              <span className={`status-badge status-${order.status}`}>{order.status}</span>
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Purchased</span>
            <span className="detail-value">{formatDate(order.createdAt)}</span>
          </div>
          {order.totalCents != null && (
            <div className="detail-item">
              <span className="detail-label">Price</span>
              <span className="detail-value">{formatPrice(order.totalCents / 100)}</span>
            </div>
          )}
          <div className="detail-item">
            <span className="detail-label">Store Ref</span>
            <span className="detail-value">{order.storeOrderNumber}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Receipt</span>
            <span className="detail-value">
              <a href={order.receiptUrl} target="_blank" rel="noreferrer" className="card-link">View receipt</a>
            </span>
          </div>
        </div>

        <h2 className="section-title" style={{ marginTop: 32 }}>Refund</h2>
        {state === "eligible" && (
          <>
            <p className="generate-codes-hint">
              You can request a full refund
              {order.totalCents != null && <> of <strong>{formatPrice(order.totalCents / 100)}</strong></>}
              {" "}for this order until <strong>{formatDate(order.refundableUntil)}</strong>.
              Refunding removes the licenses, credits and points from this order from your account
              immediately.
            </p>
            {(order.restoresOnRefund?.length ?? 0) > 0 && (
              <div className="generate-codes-hint">
                This order was an upgrade — refunding restores what you traded in (iLok assets won't be restored - request them later if you want them back):
                <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                  {order.restoresOnRefund!.map((it) => (
                    <RestoredEntitlementItem key={it.entitlementId} skuId={it.skuId} productIds={it.productIds} accessToken={accessToken} />
                  ))}
                </ul>
              </div>
            )}
            <button className="btn-danger" style={{ marginTop: 12 }} onClick={() => setRefundDialogOpen(true)}>Request refund</button>
          </>
        )}
        {state === "expired" && (
          <p className="generate-codes-hint">
            Refunds are available for 14 days after purchase. This order's refund window closed
            on <strong>{formatDate(order.refundableUntil)}</strong>. If something is wrong with your
            purchase, contact support and we'll sort it out.
          </p>
        )}
        {state === "refunded" && (
          <p className="generate-codes-hint">
            This order has been refunded and its licenses have been removed from your account.
            Refunds usually appear on your statement within 5–10 business days.
          </p>
        )}

        {refundDialogOpen && (
          <RequestRefundDialog order={order} accessToken={accessToken} onClose={() => setRefundDialogOpen(false)} />
        )}
      </div>
    )
  }

  return (
    <div className="page">
      <p className="empty-state"><Spinner size={16} /> Loading order…</p>
    </div>
  )
}

// Confirmation for the self-service refund. Irreversible from the user's
// side (the money goes back, the licenses go away), so the confirm button is
// the destructive style and the dialog can't be dismissed mid-request.
function RequestRefundDialog({ order, accessToken, onClose }: {
  order: Order
  accessToken: string
  onClose: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const dispatch = useDispatch<AppDispatch>()
  const [step, setStep] = useState<"confirm" | "refunding" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => { dialogRef.current?.showModal() }, [])

  const handleRefund = async () => {
    setStep("refunding")
    setErrorMessage("")
    try {
      await dispatch(refundOrderThunk({ id: order.id, accessToken })).unwrap()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog ref={dialogRef} className="redeem-dialog" onClose={onClose}
      {...backdropDismiss(() => { if (step !== "refunding") dialogRef.current?.close() })}>
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close" disabled={step === "refunding"}>&times;</button>
        <h2 className="redeem-dialog-title">Refund order #{order.storeOrderNumber}</h2>
        <p>
          The full amount is refunded to your original payment method. The licenses from this
          order stop working right away and are removed from your account, along with any
          credits or points it granted. This cannot be undone.
        </p>
        {step === "error" && errorMessage && (<p className="generate-codes-error">{errorMessage}</p>)}
        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "refunding"}>Keep order</button>
          <button className="btn-danger" disabled={step === "refunding"} onClick={handleRefund}>
            {step === "refunding" ? <><Spinner size={12} /> Refunding…</> : "Refund order"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
